import { type ProgramPublic, ProgramsService } from "@/client"
import { PageSkeleton } from "@/components/Layout"
import {
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Badge,
  Box,
  Card,
  Flex,
  HStack,
  IconButton,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { useState } from "react"
import { FaEdit, FaTrash, FaUsers } from "react-icons/fa"
import { DeleteProgram } from "./DeleteProgram"
import { ProgramForm } from "./ProgramForm"

interface ProgramsListProps {
  isAdmin: boolean
}

export function ProgramsList({ isAdmin }: ProgramsListProps) {
  const [editingProgram, setEditingProgram] = useState<ProgramPublic | null>(
    null,
  )
  const [deletingProgram, setDeletingProgram] = useState<ProgramPublic | null>(
    null,
  )

  const { data: programsData, isLoading } = useQuery({
    queryKey: ["programs"],
    queryFn: () => ProgramsService.listPrograms({}),
  })

  // Fetch user's memberships to check for admin role
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "green"
      case "inactive":
        return "orange"
      case "archived":
        return "gray"
      default:
        return "gray"
    }
  }

  // Only superusers or org admins can manage programs
  if (isLoading) {
    return <PageSkeleton variant="table" rows={4} />
  }

  const programs = programsData?.data || []

  return (
    <Box data-testid="programs-list">
      {/* Desktop */}
      <Box display={{ base: "none", md: "block" }}>
        <Table.Root data-testid="programs-table">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Description</Table.ColumnHeader>
              <Table.ColumnHeader>Dates</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Enrolled</Table.ColumnHeader>
              {isAdmin && <Table.ColumnHeader>Actions</Table.ColumnHeader>}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {programs.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={isAdmin ? 6 : 5}>
                  <Text color="fg.muted" textAlign="center" py={4}>
                    No programs found
                  </Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              programs.map((program) => (
                <Table.Row
                  key={program.id}
                  data-testid="program-row"
                  data-program-id={program.id}
                >
                  <Table.Cell>
                    <Link to="/programs/$id" params={{ id: program.id }}>
                      <Text fontWeight={500}>{program.name}</Text>
                    </Link>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm" color="fg.muted" lineClamp={2}>
                      {program.description || "—"}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm">
                      {program.start_date && program.end_date
                        ? `${new Date(
                            program.start_date,
                          ).toLocaleDateString()} - ${new Date(
                            program.end_date,
                          ).toLocaleDateString()}`
                        : "—"}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge
                      colorPalette={
                        getStatusColor(program.status || "active") as
                          | "green"
                          | "orange"
                          | "gray"
                      }
                    >
                      {program.status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <HStack gap={1}>
                      <FaUsers />
                      <Text>{program.enrolled_count || 0}</Text>
                    </HStack>
                  </Table.Cell>
                  {isAdmin && (
                    <Table.Cell>
                      <HStack gap={2}>
                        <IconButton
                          aria-label="Edit program"
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingProgram(program)}
                        >
                          <FaEdit />
                        </IconButton>
                        <IconButton
                          aria-label="Delete program"
                          size="sm"
                          variant="ghost"
                          colorPalette="red"
                          onClick={() => setDeletingProgram(program)}
                        >
                          <FaTrash />
                        </IconButton>
                      </HStack>
                    </Table.Cell>
                  )}
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Mobile cards */}
      {programs.length === 0 ? (
        <Text
          display={{ base: "block", md: "none" }}
          color="fg.muted"
          textAlign="center"
          py={4}
        >
          No programs found
        </Text>
      ) : (
        <VStack display={{ base: "flex", md: "none" }} gap={3} align="stretch">
          {programs.map((program) => (
            <Card.Root
              key={program.id}
              data-testid="program-row"
              data-program-id={program.id}
            >
              <Card.Body>
                <Link to="/programs/$id" params={{ id: program.id }}>
                  <Text fontWeight={500}>{program.name}</Text>
                </Link>
                {program.description && (
                  <Text fontSize="sm" color="fg.muted" lineClamp={2} mt={1}>
                    {program.description}
                  </Text>
                )}
                <Text fontSize="sm" color="fg.muted" mt={1}>
                  {program.start_date && program.end_date
                    ? `${new Date(
                        program.start_date,
                      ).toLocaleDateString()} – ${new Date(
                        program.end_date,
                      ).toLocaleDateString()}`
                    : "No dates set"}
                </Text>
                <Flex align="center" justify="space-between" mt={3}>
                  <HStack gap={2}>
                    <Badge
                      colorPalette={
                        getStatusColor(program.status || "active") as
                          | "green"
                          | "orange"
                          | "gray"
                      }
                    >
                      {program.status}
                    </Badge>
                    <HStack gap={1}>
                      <FaUsers />
                      <Text fontSize="sm">{program.enrolled_count || 0}</Text>
                    </HStack>
                  </HStack>
                  {isAdmin && (
                    <HStack gap={2}>
                      <IconButton
                        aria-label="Edit program"
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingProgram(program)}
                      >
                        <FaEdit />
                      </IconButton>
                      <IconButton
                        aria-label="Delete program"
                        size="sm"
                        variant="ghost"
                        colorPalette="red"
                        onClick={() => setDeletingProgram(program)}
                      >
                        <FaTrash />
                      </IconButton>
                    </HStack>
                  )}
                </Flex>
              </Card.Body>
            </Card.Root>
          ))}
        </VStack>
      )}

      {/* Edit Program Modal */}
      <DialogRoot
        open={!!editingProgram}
        onOpenChange={(e) => !e.open && setEditingProgram(null)}
        size="lg"
      >
        <DialogBackdrop />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Program</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {editingProgram && (
              <ProgramForm
                program={editingProgram}
                onSuccess={() => setEditingProgram(null)}
              />
            )}
          </DialogBody>
        </DialogContent>
      </DialogRoot>

      {/* Delete Program Confirmation */}
      {deletingProgram && (
        <DeleteProgram
          program={deletingProgram}
          onClose={() => setDeletingProgram(null)}
        />
      )}
    </Box>
  )
}
